import type { Project, UserProject } from '@teachur/http-client';

type CourseProject = Project & {
  title?: string | null;
};

interface StudentWithProjects {
  userProjects?: UserProject[] | null;
}

interface CourseWithProjects {
  projects?: CourseProject[] | null;
}

export interface StudentCourseProjectProgressData {
  student?: unknown;
  course?: unknown;
}

export interface ProjectSubmissionSummary {
  id: number | string;
  status: string;
  fileName: string;
  fileUrl?: string | null;
  reviewDate: string;
  instructorNote: string;
}

export interface ProjectSummary {
  projectName: string;
  status: string;
  submissions: ProjectSubmissionSummary[];
}

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
};

const formatDate = (date?: string | null) => {
  if (!date) return '—';

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) return '—';

  return parsedDate.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS);
};

const getTimestamp = (date?: string | null) => {
  if (!date) return 0;

  const timestamp = new Date(date).getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getStatusLabel = (status?: UserProject['status'] | string | null) => {
  if (!status) return 'Submitted';

  const normalizedStatus = String(status).toLowerCase();

  if (normalizedStatus === 'approved') return 'Approved';
  if (normalizedStatus === 'notyet') return 'Needs revision';
  if (normalizedStatus === 'rejected') return 'Rejected';
  if (normalizedStatus === 'pending') return 'Pending review';
  if (normalizedStatus === 'inreview' || normalizedStatus === 'in_review') return 'In review';

  return String(status);
};

const getCourseProjectIds = (course?: CourseWithProjects) => {
  return (
    course?.projects
      ?.map((project) => project.id)
      .filter((projectId): projectId is number => typeof projectId === 'number') ?? []
  );
};

const getProjectName = (course?: CourseWithProjects, projectId?: number | null) => {
  const project = course?.projects?.find((courseProject) => courseProject.id === projectId);

  return project?.name ?? project?.title ?? 'Project';
};

const getSelectedSubmissions = (userProjects: UserProject[], courseProjectIds: number[]) => {
  return userProjects
    .filter((userProject) => courseProjectIds.includes(userProject.projectId))
    .sort((a, b) => {
      return getTimestamp(b.updatedAt ?? b.createdAt) - getTimestamp(a.updatedAt ?? a.createdAt);
    });
};

export const getProjectSummary = (
  progressData?: StudentCourseProjectProgressData | null
): ProjectSummary => {
  const student = progressData?.student as StudentWithProjects | undefined;
  const course = progressData?.course as CourseWithProjects | undefined;

  const courseProjectIds = getCourseProjectIds(course);
  const submissions = getSelectedSubmissions(student?.userProjects ?? [], courseProjectIds);

  const latestSubmission = submissions[0];
  const selectedProjectId = latestSubmission?.projectId ?? courseProjectIds[0] ?? null;

  return {
    projectName: getProjectName(course, selectedProjectId),
    status: latestSubmission ? getStatusLabel(latestSubmission.status) : 'Not submitted',
    submissions: submissions.map((submission) => ({
      id: submission.id,
      status: getStatusLabel(submission.status),
      fileName: submission.file?.pid ?? 'No file available',
      fileUrl: submission.file?.url ?? null,
      reviewDate: formatDate(submission.updatedAt ?? submission.createdAt),
      instructorNote: submission.note ?? 'No instructor note added.',
    })),
  };
};
